import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { useAsync } from 'react-async';
import {
  KoobButton,
  KoobEmptyState,
  KoobInputSelect,
  KoobInputText,
  KoobModal,
  KoobSkeleton,
} from '@koob/ui';
import { formatDateTime } from '@koob/dates';
import {
  getCurrencyExchangeRates,
  modifyCurrencyExchangeRates,
} from 'api/node/currencyExchangeRates';
import { getCurrencies } from 'api/node/currencies';
import { useSnack } from 'hooks';
import { formatCurrencyLabel } from 'utils';

const formatDate = (date) => (date ? formatDateTime(date, 'DD/MM/YYYY') : '');
const pairKey = (from, to) => `${from}>${to}`;

// A saved rate never changes (prices already computed keep pointing at it):
// "Change" archives it and adds a new one for the same pair, in one save.
// The API only reads new rows (no id) and rows with an id to archive.
function validate(values, activeRates, t) {
  const taken = new Set(
    activeRates
      .filter(({ id }) => !values.archivedIds.includes(id))
      .map(({ fromCurrency, toCurrency }) => pairKey(fromCurrency, toCurrency)),
  );
  const added = values.added.map(({ fromCurrency, toCurrency, rate }) => {
    const error = {};
    if (!fromCurrency) error.fromCurrency = t('errors:required');
    if (!toCurrency) error.toCurrency = t('errors:required');
    if (rate === '' || rate === undefined) error.rate = t('errors:required');
    else if (!(Number(rate) > 0)) error.rate = t('form.positiveRate');
    if (fromCurrency && toCurrency) {
      const key = pairKey(fromCurrency, toCurrency);
      if (fromCurrency === toCurrency) error.toCurrency = t('form.samePair');
      else if (taken.has(key)) {
        error.toCurrency = t('form.duplicatePair', {
          from: fromCurrency,
          to: toCurrency,
        });
      }
      taken.add(key);
    }
    return Object.keys(error).length ? error : undefined;
  });
  return added.some(Boolean) ? { added } : {};
}

function RateText({ rate }) {
  return (
    <span className="font-mono text-sm text-k-h">
      1 {rate.fromCurrency} = {rate.rate} {rate.toCurrency}
    </span>
  );
}

function FieldError({ children }) {
  return children ? (
    <p className="m-0 mt-1 text-xs text-red-700">{children}</p>
  ) : null;
}

function NewRateRow({
  index,
  row,
  error,
  currencyOptions,
  onChange,
  onRemove,
}) {
  const { t } = useTranslation('currencyExchangeRateModal');
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-k-p">1</span>
        <div className="w-28">
          <KoobInputSelect
            searchable
            size="sm"
            options={currencyOptions}
            value={row.fromCurrency}
            onValueChange={(value) => onChange('fromCurrency', value)}
            placeholder={t('form.from')}
            error={Boolean(error?.fromCurrency)}
            testId={`exchange-rate-${index}-from`}
          />
        </div>
        <span className="text-sm text-k-p">=</span>
        <div className="w-32">
          <KoobInputText
            size="sm"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            placeholder="1.0000"
            value={row.rate}
            onChange={(e) => onChange('rate', e.target.value)}
            error={Boolean(error?.rate)}
            aria-label={t('form.rate')}
          />
        </div>
        <div className="w-28">
          <KoobInputSelect
            searchable
            size="sm"
            options={currencyOptions}
            value={row.toCurrency}
            onValueChange={(value) => onChange('toCurrency', value)}
            placeholder={t('form.to')}
            error={Boolean(error?.toCurrency)}
            testId={`exchange-rate-${index}-to`}
          />
        </div>
        <KoobButton
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label={t('form.remove')}
        >
          <i className="fa-regular fa-xmark" />
        </KoobButton>
      </div>
      <FieldError>
        {error?.fromCurrency || error?.rate || error?.toCurrency}
      </FieldError>
    </li>
  );
}

// Rates and form for one opening: keyed by the opening, so each one loads
// fresh rates and starts from a clean form.
function ExchangeRatesContent({
  onClose,
  dmcCurrency,
  isAllowedToManage,
  organizationId,
}) {
  const { t } = useTranslation('currencyExchangeRateModal');
  const { notify } = useSnack();

  const rates = useAsync({
    promiseFn: getCurrencyExchangeRates,
    filters: { organizationId },
    watch: organizationId,
  });
  const currencies = useAsync({ promiseFn: getCurrencies });

  const allRates = rates.data?.data ?? [];
  const activeRates = allRates.filter(({ archived }) => !archived);
  const archivedRates = allRates.filter(({ archived }) => archived);
  const currencyOptions = (currencies.data?.data ?? []).map(({ name }) => ({
    value: name,
    label: formatCurrencyLabel(name),
  }));

  const newRow = (fromCurrency = dmcCurrency, toCurrency) => ({
    fromCurrency,
    toCurrency,
    rate: '',
  });

  const handleSubmit = async ({ archivedIds, added }) => {
    try {
      await modifyCurrencyExchangeRates({ organizationId }, [
        ...activeRates
          .filter(({ id }) => archivedIds.includes(id))
          .map((rate) => ({ ...rate, archived: true })),
        ...added.map((row) => ({
          ...row,
          rate: Number(row.rate),
          archived: false,
        })),
      ]);
      notify(t('form.currenciesModifiedSuccessfully'));
      rates.reload();
      onClose();
    } catch (e) {
      notify(t('form.saveError'), { type: 'error' });
    }
  };

  const loading = rates.isPending || currencies.isPending;

  return (
    <Formik
      initialValues={{ archivedIds: [], added: [] }}
      validate={(values) => validate(values, activeRates, t)}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        submitCount,
        setFieldValue,
        isSubmitting,
        dirty,
      }) => {
        const archive = (id) =>
          setFieldValue('archivedIds', [...values.archivedIds, id]);
        const unarchive = (id) =>
          setFieldValue(
            'archivedIds',
            values.archivedIds.filter((other) => other !== id),
          );
        const add = (row) => setFieldValue('added', [...values.added, row]);
        const isEmpty = !activeRates.length && !values.added.length;

        return (
          <Form className="flex max-h-[85vh] flex-col">
            <header className="border-b border-gray-200 px-6 pb-4 pt-5 pr-14">
              <h2 className="m-0 text-lg font-semibold text-k-h">
                {t('title')}
              </h2>
              <p className="m-0 mt-1 text-sm text-k-p">
                {dmcCurrency
                  ? t('description', { currency: dmcCurrency })
                  : t('descriptionNoCurrency')}
              </p>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              {loading ? (
                <KoobSkeleton shape="form" />
              ) : rates.error ? (
                <KoobEmptyState
                  variant="plain"
                  icon={
                    <i className="fa-regular fa-triangle-exclamation text-2xl" />
                  }
                  title={t('loadError')}
                >
                  <KoobButton onClick={rates.reload}>{t('retry')}</KoobButton>
                </KoobEmptyState>
              ) : isEmpty ? (
                <KoobEmptyState
                  variant="plain"
                  icon={
                    <i className="fa-regular fa-arrow-right-arrow-left text-2xl" />
                  }
                  title={t('empty.title')}
                  description={t('empty.description')}
                >
                  {isAllowedToManage && (
                    <KoobButton onClick={() => add(newRow())}>
                      <i className="fa-regular fa-plus mr-1.5" />
                      {t('form.newExchangeRate')}
                    </KoobButton>
                  )}
                </KoobEmptyState>
              ) : (
                <>
                  <ul className="m-0 list-none divide-y divide-gray-100 p-0">
                    {activeRates.map((rate) => {
                      const leaving = values.archivedIds.includes(rate.id);
                      return (
                        <li
                          key={rate.id}
                          className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3"
                        >
                          <div
                            className={leaving ? 'line-through opacity-50' : ''}
                          >
                            <RateText rate={rate} />
                          </div>
                          <span className="text-xs text-k-p">
                            {leaving
                              ? t('form.willBeArchived')
                              : t('form.since', {
                                  date: formatDate(rate.createdAt),
                                })}
                          </span>
                          {isAllowedToManage && (
                            <div className="ml-auto flex gap-1">
                              {leaving ? (
                                <KoobButton
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => unarchive(rate.id)}
                                >
                                  {t('form.undo')}
                                </KoobButton>
                              ) : (
                                <>
                                  <KoobButton
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      archive(rate.id);
                                      add(
                                        newRow(
                                          rate.fromCurrency,
                                          rate.toCurrency,
                                        ),
                                      );
                                    }}
                                  >
                                    {t('form.change')}
                                  </KoobButton>
                                  <KoobButton
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => archive(rate.id)}
                                  >
                                    {t('form.archive')}
                                  </KoobButton>
                                </>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                    {values.added.map((row, idx) => (
                      <NewRateRow
                        key={idx}
                        index={idx}
                        row={row}
                        // Errors show once the user tried to save.
                        error={submitCount ? errors.added?.[idx] : undefined}
                        currencyOptions={currencyOptions}
                        onChange={(field, value) =>
                          setFieldValue(`added.${idx}.${field}`, value)
                        }
                        onRemove={() =>
                          setFieldValue(
                            'added',
                            values.added.filter((_, other) => other !== idx),
                          )
                        }
                      />
                    ))}
                  </ul>
                  {isAllowedToManage && (
                    <div className="pt-2">
                      <KoobButton
                        variant="ghost"
                        size="sm"
                        onClick={() => add(newRow())}
                      >
                        <i className="fa-regular fa-plus mr-1.5" />
                        {t('form.newExchangeRate')}
                      </KoobButton>
                    </div>
                  )}
                </>
              )}

              {!loading && archivedRates.length > 0 && (
                <details className="mt-4 border-t border-gray-100 pt-3">
                  <summary className="cursor-pointer text-sm font-medium text-k-p">
                    {t('form.archivedRates', { count: archivedRates.length })}
                  </summary>
                  <ul className="m-0 mt-2 list-none space-y-2 p-0">
                    {archivedRates.map((rate) => (
                      <li
                        key={rate.id}
                        className="flex flex-wrap items-center gap-x-4 opacity-70"
                      >
                        <RateText rate={rate} />
                        <span className="text-xs text-k-p">
                          {formatDate(rate.createdAt)} –{' '}
                          {formatDate(rate.discardedAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>

            <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
              {values.archivedIds.length > 0 && (
                <p className="m-0 mr-auto text-xs text-k-p">
                  {t('form.archiveNotice')}
                </p>
              )}
              {isAllowedToManage ? (
                <>
                  <KoobButton onClick={onClose}>{t('misc:cancel')}</KoobButton>
                  <KoobButton
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    disabled={!dirty || loading}
                  >
                    {t('misc:save')}
                  </KoobButton>
                </>
              ) : (
                <KoobButton onClick={onClose}>{t('misc:close')}</KoobButton>
              )}
            </footer>
          </Form>
        );
      }}
    </Formik>
  );
}

// Stays mounted and follows `open`, so it closes with KoobModal's animation;
// the content stays on screen while it fades out.
export default function ExchangeRatesModal({ open, onClose, ...props }) {
  const [opening, setOpening] = useState(0);
  useEffect(() => {
    if (open) setOpening((count) => count + 1);
  }, [open]);

  return (
    <KoobModal
      open={open}
      setOpen={onClose}
      widthClass="sm:max-w-2xl"
      paddingClass="p-0"
      showCloseIcon
    >
      {opening > 0 && (
        <ExchangeRatesContent key={opening} onClose={onClose} {...props} />
      )}
    </KoobModal>
  );
}
